import type { OutputLayer } from '@/components/preview/output-layer-preview';

/** Layer `data` carries signed media URLs — expire entries well before those do. */
export const PREVIEW_LAYERS_CACHE_TTL_MS = 5 * 60 * 1000;
const MAX_ENTRIES = 32;

type Entry = { storedAt: number; layers: OutputLayer[] };

/** Look-ahead layers by request key, so a prefetched cue row swaps in without a round trip. */
export class PreviewLayersCache {
  private readonly entries = new Map<string, Entry>();

  constructor(private readonly ttlMs: number = PREVIEW_LAYERS_CACHE_TTL_MS) {}

  get(key: string, nowMs: number = Date.now()): OutputLayer[] | undefined {
    const entry = this.entries.get(key);
    if (!entry) {
      return undefined;
    }
    if (nowMs - entry.storedAt >= this.ttlMs) {
      this.entries.delete(key);
      return undefined;
    }
    return entry.layers;
  }

  has(key: string, nowMs: number = Date.now()): boolean {
    return this.get(key, nowMs) != null;
  }

  delete(key: string): void {
    this.entries.delete(key);
  }

  set(key: string, layers: OutputLayer[], nowMs: number = Date.now()): void {
    if (this.entries.size >= MAX_ENTRIES) {
      const oldest = this.entries.keys().next();
      if (!oldest.done) {
        this.entries.delete(oldest.value);
      }
    }
    this.entries.set(key, { storedAt: nowMs, layers });
  }
}
